import React, { useState, useEffect } from 'react';
import { I, BrandMark, TopBar } from './shared';
import { api } from './api';

// Deterministic color palette — assigned by team name hash so same team always same color
const PALETTE = [
  '#0B1F3A','#1b3a66','#7a1f2e','#1F5BA8','#3a1d6b',
  '#0e6b4a','#4b1d1d','#0d4a6b','#5a1f4f','#1d3557',
];

function teamColor(name) {
  if (!name) return PALETTE[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

function teamInitials(name) {
  if (!name) return '?';
  return name.split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 3);
}

export default function TeamPicker({ onPick, onLogout, theme, onToggleTheme }) {
  const [teams, setTeams] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [error, setError] = useState(null);
  const [gamesError, setGamesError] = useState(null);

  useEffect(() => {
    api.getTeams()
      .then(data => setTeams(data.teams || []))
      .catch(err => setError(err.message));
  }, []);

  async function pick(team) {
    setLoadingId(team.id);
    setGamesError(null);
    try {
      const data = await api.getGames(team.public_id || team.id);
      onPick({
        ...team,
        teamId:    team.public_id || team.id,
        games:     data.games || [],
        gameCount: (data.games || []).length,
        color:     team.color    || teamColor(team.name),
        initials:  team.initials || teamInitials(team.name),
      });
    } catch (err) {
      setGamesError(err.message);
      setLoadingId(null);
    }
  }

  const SEASON_ORDER = { spring: 0, summer: 1, fall: 2, winter: 3 };

  return (
    <div className="page">
      <TopBar
        left={<BrandMark/>}
        right={<button className="btn btn-ghost" onClick={onLogout}><I.Logout/> Sign out</button>}
        theme={theme}
        onToggleTheme={onToggleTheme}
      />
      <main className="page-main">
        <div className="shell-narrow">
          <div className="section-h">
            <div>
              <h1>Your teams</h1>
              <p>Pick a team to load the season.</p>
            </div>
          </div>

          {gamesError && (
            <div className="banner banner-error" style={{marginBottom: 12}}>{gamesError}</div>
          )}

          {/* Skeleton */}
          {!teams && !error && (
            <div className="team-grid">
              {[1,2,3,4].map(i => (
                <div key={i} className="team-row" style={{cursor: 'default'}}>
                  <div className="skel" style={{width: 44, height: 44, borderRadius: 11, flexShrink: 0}}/>
                  <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: 6}}>
                    <div className="skel" style={{height: 14, width: '70%'}}/>
                    <div className="skel" style={{height: 10, width: '40%'}}/>
                  </div>
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="empty">
              <h3>Couldn't load teams</h3>
              <p>{error}</p>
            </div>
          )}

          {teams && teams.length === 0 && (
            <div className="empty">
              <h3>No softball teams found</h3>
              <p>This GameChanger account doesn't have any active softball teams.</p>
            </div>
          )}

          {teams && teams.length > 0 && (() => {
            const byYear = new Map();
            teams.forEach(t => {
              const y = t.season_year || 'Unknown';
              if (!byYear.has(y)) byYear.set(y, new Map());
              const ys = byYear.get(y);
              if (!ys.has(t.season_name)) ys.set(t.season_name, []);
              ys.get(t.season_name).push(t);
            });
            const years = [...byYear.keys()].sort((a, b) => b - a);

            return (
              <div className="picker-groups">
                {years.map(year => {
                  const seasons = [...byYear.get(year).entries()]
                    .sort((a, b) => (SEASON_ORDER[a[0]] ?? 99) - (SEASON_ORDER[b[0]] ?? 99));
                  return (
                    <section key={year} className="picker-year">
                      <h2 className="picker-year-h">{year}</h2>
                      {seasons.map(([season, teamsInSeason]) => (
                        <div key={season} className="picker-season">
                          <h3 className="picker-season-h">
                            <span>{season[0].toUpperCase() + season.slice(1)}</span>
                            <span className="picker-season-count">{teamsInSeason.length}</span>
                          </h3>
                          <div className="team-grid">
                            {teamsInSeason.map(team => {
                              const color    = team.color    || teamColor(team.name);
                              const initials = team.initials || teamInitials(team.name);
                              return (
                                <button
                                  key={team.id}
                                  className="team-row"
                                  onClick={() => pick(team)}
                                  disabled={loadingId !== null}
                                >
                                  <div className="team-crest" style={{background: color}}>
                                    {initials}
                                  </div>
                                  <div className="team-info">
                                    <div className="team-name">{team.name}</div>
                                    {team.season_name && (
                                      <div className="team-meta">
                                        {team.season_name[0].toUpperCase() + team.season_name.slice(1)} {team.season_year}
                                      </div>
                                    )}
                                  </div>
                                  <div className="team-arrow">
                                    {loadingId === team.id
                                      ? <span className="dot-pulse"><i/><i/><i/></span>
                                      : <I.Right size={18}/>}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </section>
                  );
                })}
              </div>
            );
          })()}

          <p style={{fontSize: 12, color: 'var(--text-3)', textAlign: 'center', marginTop: 24}}>
            Showing softball teams from your most recent season.
          </p>
        </div>
      </main>
    </div>
  );
}
